'use client'

import { useState, useEffect, useRef } from 'react'
import { User } from '@/types'

// Extended KYC data structure for the component
// KycData interface removed for now

// ExtendedKycData interface removed for now

// Document structure for the component
interface DocumentFile {
  file_url: string
  file_name: string
  status: string
}

// Component-specific KYC data structure
interface ComponentKycData {
  personal_info: {
    first_name: string
    last_name: string
    phone: string
    address: string
    city: string
    state: string
    postal_code: string
    date_of_birth: string
    nationality: string
    occupation: string
    employer: string
    monthly_income: number
  }
  documents: {
    id_card: DocumentFile
    proof_of_address: DocumentFile
    proof_of_income: DocumentFile
    bank_statement: DocumentFile
  }
  verification_status: 'pending' | 'in_review' | 'approved' | 'rejected' | 'submitted'
}

type Step = 'personal' | 'documents' | 'review' | 'submitted'

interface KycStepperProps {
  user: User
  isVerified?: boolean
  onCompleted?: () => void
}

export default function KycStepper({ user, isVerified: _isVerified = false, onCompleted }: KycStepperProps) {
  const [step, setStep] = useState<Step>('personal')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [uploading, setUploading] = useState<string | null>(null)
  
  const fileInputRefs = {
    id_card: useRef<HTMLInputElement>(null),
    proof_of_address: useRef<HTMLInputElement>(null),
    proof_of_income: useRef<HTMLInputElement>(null),
    bank_statement: useRef<HTMLInputElement>(null)
  }

  const [kycData, setKycData] = useState<ComponentKycData>({
    personal_info: {
      first_name: '',
      last_name: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      date_of_birth: '',
      nationality: '',
      occupation: '',
      employer: '',
      monthly_income: 0
    },
    documents: {
      id_card: { file_url: '', file_name: '', status: 'pending' },
      proof_of_address: { file_url: '', file_name: '', status: 'pending' },
      proof_of_income: { file_url: '', file_name: '', status: 'pending' },
      bank_statement: { file_url: '', file_name: '', status: 'pending' }
    },
    verification_status: 'pending'
  })

  // Load existing KYC data if available
  useEffect(() => {
    if (user.kyc_data) {
      // Convert from database format to component format
      const dbData = user.kyc_data as any
      if (dbData) {
        const convertedData: ComponentKycData = {
          personal_info: {
            first_name: dbData.firstName || dbData.personal_info?.first_name || '',
            last_name: dbData.lastName || dbData.personal_info?.last_name || '',
            phone: dbData.phone || dbData.personal_info?.phone || '',
            address: dbData.address || dbData.personal_info?.address || '',
            city: dbData.city || dbData.personal_info?.city || '',
            state: dbData.state || dbData.personal_info?.state || '',
            postal_code: dbData.postal_code || dbData.personal_info?.postal_code || '',
            date_of_birth: dbData.dateOfBirth || dbData.personal_info?.date_of_birth || '',
            nationality: dbData.nationality || dbData.personal_info?.nationality || '',
            occupation: dbData.occupation || dbData.personal_info?.occupation || '',
            employer: dbData.employer || dbData.personal_info?.employer || '',
            monthly_income: dbData.monthly_income || dbData.personal_info?.monthly_income || 0
          },
          documents: {
            id_card: {
              file_url: dbData.documents?.idDocument || dbData.documents?.id_card?.file_url || '',
              file_name: dbData.documents?.id_card?.file_name || 'ID Document',
              status: dbData.documents?.id_card?.status || 'pending'
            },
            proof_of_address: {
              file_url: dbData.documents?.proofOfAddress || dbData.documents?.proof_of_address?.file_url || '',
              file_name: dbData.documents?.proof_of_address?.file_name || 'Proof of Address',
              status: dbData.documents?.proof_of_address?.status || 'pending'
            },
            proof_of_income: {
              file_url: dbData.documents?.employmentLetter || dbData.documents?.proof_of_income?.file_url || '',
              file_name: dbData.documents?.proof_of_income?.file_name || 'Proof of Income',
              status: dbData.documents?.proof_of_income?.status || 'pending'
            },
            bank_statement: {
              file_url: dbData.documents?.bankStatement || dbData.documents?.bank_statement?.file_url || '',
              file_name: dbData.documents?.bank_statement?.file_name || 'Bank Statement',
              status: dbData.documents?.bank_statement?.status || 'pending'
            }
          },
          verification_status: dbData.verification_status || 'pending'
        }
        setKycData(convertedData)
        
        if (convertedData.verification_status === 'submitted') {
          setStep('submitted')
        }
      }
    }
  }, [user.kyc_data])

  // Handle personal info changes
  const handlePersonalInfoChange = (field: string, value: string | number) => {
    setKycData(prev => ({
      ...prev,
      personal_info: {
        ...prev.personal_info,
        [field]: value
      }
    }))
  }

  // Handle file upload
  const handleFileUpload = async (documentType: keyof ComponentKycData['documents'], file: File) => {
    if (!file) return

    // Check if KYC is already pending (check backend status, not internal state)
    const currentKycStatus = user?.kyc?.status || user?.kyc_data?.verification_status
    const isPending = currentKycStatus === 'pending' || currentKycStatus === 'in_review' || kycData.verification_status === 'submitted'
    
    if (isPending) {
      setMessage('Your KYC is pending review. You cannot upload documents until the review is complete.')
      return
    }

    setUploading(documentType)
    setMessage('')

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('Please log in to upload documents')
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      
      // Upload file to Cloudinary via backend
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', documentType)
      
      const response = await fetch(`${apiUrl}/upload/single`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })
      
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Upload failed')
      }
      
      // Update KYC data with the uploaded file URL
      setKycData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: {
            file_url: data.file.url,
            file_name: data.file.originalName || file.name,
            status: 'pending'
          }
        }
      }))

      setMessage(`${documentType.replace(/_/g, ' ')} uploaded successfully!`)
    } catch (error) {
      console.error('Upload error:', error)
      setMessage(`Failed to upload ${documentType.replace(/_/g, ' ')}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setUploading(null)
    }
  }

  // Validate current step
  const validateStep = (currentStep: Step): boolean => {
    switch (currentStep) {
      case 'personal':
        const personal = kycData.personal_info
        return !!(personal.first_name && personal.last_name && personal.phone && 
                 personal.address && personal.city && personal.state && 
                 personal.date_of_birth && personal.nationality && 
                 personal.occupation && personal.monthly_income > 0)
      
      case 'documents':
        const docs = kycData.documents
        return !!(docs.id_card.file_url && docs.proof_of_address.file_url && 
                 docs.proof_of_income.file_url && docs.bank_statement.file_url)
      
      default:
        return true
    }
  }

  // Navigate to next step
  const nextStep = () => {
    if (!validateStep(step)) {
      setMessage('Please complete all required fields before proceeding.')
      return
    }

    if (step === 'personal') {
      setStep('documents')
    } else if (step === 'documents') {
      setStep('review')
    }
    setMessage('')
  }

  // Navigate to previous step
  const prevStep = () => {
    if (step === 'documents') {
      setStep('personal')
    } else if (step === 'review') {
      setStep('documents')
    }
    setMessage('')
  }

  // Submit KYC for verification
  const submitKYC = async () => {
    // Check if KYC is already pending (check backend status, not internal state)
    const currentKycStatus = user?.kyc?.status || user?.kyc_data?.verification_status
    const isPending = currentKycStatus === 'pending' || currentKycStatus === 'in_review' || kycData.verification_status === 'submitted'
    
    if (isPending) {
      setMessage('Your KYC is already pending review. You cannot submit again until the review is complete.')
      return
    }

    if (!validateStep(step)) {
      setMessage('Please complete all required fields before submitting.')
      return
    }

    setLoading(true)
    setMessage('')

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('Please log in to submit KYC')
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      
      // First, update user profile with personal info
      const profileResponse = await fetch(`${apiUrl}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: kycData.personal_info.first_name,
          lastName: kycData.personal_info.last_name,
          phone: kycData.personal_info.phone
        })
      })
      
      if (!profileResponse.ok) {
        const errorData = await profileResponse.json()
        throw new Error(errorData.message || 'Failed to update profile')
      }
      
      // Prepare KYC documents array for submission
      const documents = [
        { type: 'idDocument', url: kycData.documents.id_card.file_url },
        { type: 'proofOfAddress', url: kycData.documents.proof_of_address.file_url },
        { type: 'employmentLetter', url: kycData.documents.proof_of_income.file_url },
        { type: 'bankStatement', url: kycData.documents.bank_statement.file_url }
      ].filter(doc => doc.url) // Only include documents that have URLs
      
      // Submit KYC documents
      const kycResponse = await fetch(`${apiUrl}/users/upload-kyc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ documents })
      })
      
      if (!kycResponse.ok) {
        const errorData = await kycResponse.json()
        throw new Error(errorData.message || 'Failed to submit KYC')
      }

      setKycData(prev => ({
        ...prev,
        verification_status: 'submitted'
      }))

      setStep('submitted')
      setMessage('KYC submitted successfully! Your documents are under review.')
      
      if (onCompleted) {
        onCompleted()
      }
    } catch (error) {
      console.error('Submit error:', error)
      setMessage(`Failed to submit KYC: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  // Get step completion status
  const getStepStatus = (stepName: Step) => {
    if (stepName === 'personal') {
      return validateStep('personal') ? 'completed' : 'current'
    } else if (stepName === 'documents') {
      if (step === 'personal') return 'upcoming'
      return validateStep('documents') ? 'completed' : 'current'
    } else if (stepName === 'review') {
      if (step === 'personal' || step === 'documents') return 'upcoming'
      return 'current'
    }
    return 'upcoming'
  }

  // Get step status color
  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-600'
      case 'current':
        return 'bg-blue-600'
      case 'upcoming':
        return 'bg-gray-300'
      default:
        return 'bg-gray-300'
    }
  }

  // Check KYC status from user object (only check backend statuses, not internal 'submitted')
  const kycStatus = user?.kyc?.status || user?.kyc_data?.verification_status
  const hasPendingKyc = kycStatus === 'pending' || kycStatus === 'in_review' || kycData.verification_status === 'submitted'

  if (user.is_verified) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <div className="text-green-600 text-4xl mb-2">✅</div>
        <h3 className="text-lg font-semibold text-green-900 mb-2">Verification Complete</h3>
        <p className="text-green-700">Your account has been verified successfully!</p>
      </div>
    )
  }

  // Show pending message if KYC is pending or in review
  if (hasPendingKyc || kycData.verification_status === 'submitted') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
        <div className="text-amber-600 text-4xl mb-2">⏳</div>
        <h3 className="text-lg font-display text-amber-900 mb-2">KYC Under Review</h3>
        <p className="text-amber-700 mb-4">Your verification documents have been submitted and are currently under review.</p>
        <p className="text-sm text-amber-600 font-medium">You cannot make changes until the review is complete. You will be notified once the review is done.</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Verification</h3>
        <p className="text-gray-600">Please provide the required information and documents to verify your identity.</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {['personal', 'documents', 'review'].map((stepName, index) => (
            <div key={stepName} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${getStepColor(getStepStatus(stepName as Step))}`}>
                {getStepStatus(stepName as Step) === 'completed' ? '✓' : index + 1}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700 capitalize">
                {stepName.replace('_', ' ')}
              </span>
              {index < 2 && (
                <div className={`w-16 h-1 mx-4 ${getStepStatus(stepName as Step) === 'completed' ? 'bg-green-600' : 'bg-gray-300'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-3 rounded-md ${
          message.includes('successfully') || message.includes('success') 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Step Content */}
      {step === 'personal' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={kycData.personal_info.first_name}
                onChange={(e) => handlePersonalInfoChange('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                value={kycData.personal_info.last_name}
                onChange={(e) => handlePersonalInfoChange('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your last name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                value={kycData.personal_info.phone}
                onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+234 801 234 5678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
              <input
                type="date"
                value={kycData.personal_info.date_of_birth}
                onChange={(e) => handlePersonalInfoChange('date_of_birth', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nationality *</label>
              <input
                type="text"
                value={kycData.personal_info.nationality}
                onChange={(e) => handlePersonalInfoChange('nationality', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Nigerian"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Occupation *</label>
              <input
                type="text"
                value={kycData.personal_info.occupation}
                onChange={(e) => handlePersonalInfoChange('occupation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Software Engineer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employer *</label>
              <input
                type="text"
                value={kycData.personal_info.employer}
                onChange={(e) => handlePersonalInfoChange('employer', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Company name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Income (₦) *</label>
              <input
                type="number"
                value={kycData.personal_info.monthly_income || ''}
                onChange={(e) => handlePersonalInfoChange('monthly_income', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="500100"
                min="0"
              />
            </div>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
            <input
              type="text"
              value={kycData.personal_info.address}
              onChange={(e) => handlePersonalInfoChange('address', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Street address"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input
                type="text"
                value={kycData.personal_info.city}
                onChange={(e) => handlePersonalInfoChange('city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Lagos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
              <input
                type="text"
                value={kycData.personal_info.state}
                onChange={(e) => handlePersonalInfoChange('state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Lagos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
              <input
                type="text"
                value={kycData.personal_info.postal_code}
                onChange={(e) => handlePersonalInfoChange('postal_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="100001"
              />
            </div>
          </div>
        </div>
      )}

      {step === 'documents' && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600 mb-4">
            Please upload clear, legible copies of the following documents. All documents must be in PDF, JPG, or PNG format and under 10MB.
          </p>
          
          {Object.entries(kycData.documents).map(([docType, doc]) => (
            <div key={docType} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 capitalize">
                  {docType.replace('_', ' ')} *
                </h4>
                {doc.file_url && (
                  <span className="text-sm text-green-600">✓ Uploaded</span>
                )}
              </div>
              
              {doc.file_url ? (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">{doc.file_name}</span>
                  <button
                    onClick={() => window.open(doc.file_url, '_blank')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View
                  </button>
                  <button
                    onClick={() => {
                      setKycData(prev => ({
                        ...prev,
                        documents: {
                          ...prev.documents,
                          [docType]: { file_url: '', file_name: '', status: 'pending' }
                        }
                      }))
                    }}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="border-dashed border-2 border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRefs[docType as keyof typeof fileInputRefs]}
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        handleFileUpload(docType as keyof ComponentKycData['documents'], file)
                      }
                    }}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRefs[docType as keyof typeof fileInputRefs].current?.click()}
                    disabled={uploading === docType}
                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  >
                    {uploading === docType ? 'Uploading...' : 'Click to upload'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    {docType === 'id_card' && 'Government-issued ID card, passport, or driver\'s license'}
                    {docType === 'proof_of_address' && 'Utility bill, bank statement, or rental agreement (last 3 months)'}
                    {docType === 'proof_of_income' && 'Payslip, employment letter, or tax return'}
                    {docType === 'bank_statement' && 'Bank statement showing regular income (last 3 months)'}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Name:</span>
                <span className="ml-2 text-gray-900">
                  {kycData.personal_info.first_name} {kycData.personal_info.last_name}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Phone:</span>
                <span className="ml-2 text-gray-900">{kycData.personal_info.phone}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Date of Birth:</span>
                <span className="ml-2 text-gray-900">{kycData.personal_info.date_of_birth}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Nationality:</span>
                <span className="ml-2 text-gray-900">{kycData.personal_info.nationality}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Occupation:</span>
                <span className="ml-2 text-gray-900">{kycData.personal_info.occupation}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Monthly Income:</span>
                <span className="ml-2 text-gray-900">₦{kycData.personal_info.monthly_income.toLocaleString()}</span>
              </div>
              <div className="md:col-span-2">
                <span className="font-medium text-gray-700">Address:</span>
                <span className="ml-2 text-gray-900">
                  {kycData.personal_info.address}, {kycData.personal_info.city}, {kycData.personal_info.state}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Documents</h4>
            <div className="space-y-2 text-sm">
              {Object.entries(kycData.documents).map(([docType, doc]) => (
                <div key={docType} className="flex items-center justify-between">
                  <span className="capitalize text-gray-700">{docType.replace('_', ' ')}:</span>
                  <span className={doc.file_url ? 'text-green-600' : 'text-red-600'}>
                    {doc.file_url ? '✓ Uploaded' : '✗ Missing'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-blue-600 text-xl mr-3">ℹ️</div>
              <div>
                <h4 className="font-medium text-blue-900 mb-2">Important Information</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• All information provided will be kept confidential and secure</li>
                  <li>• Verification typically takes 1-3 business days</li>
                  <li>• You will be notified via email once verification is complete</li>
                  <li>• Incomplete or unclear documents may delay the verification process</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={prevStep}
          disabled={step === 'personal'}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        
        {step !== 'review' ? (
          <button
            onClick={nextStep}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            Next
          </button>
        ) : (
          <button
            onClick={submitKYC}
            disabled={loading || !validateStep(step)}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Submit for Verification'}
          </button>
        )}
      </div>
    </div>
  )
}



